import classes from "./MainPage.module.css";
import { useEffect, useState } from "react";
import MainTitle from "../../components/Main/MainTitle/MainTitle";
import MainTop from "../../components/Main/MainTop/MainTop";
import MainStatusFilters from "../../components/Main/MainStatusFilters/MainStatusFilters";
import MainResultsTable from "../../components/Main/MainResults/MainResultsTable/MainResultsTable";
import { Employee, Unit } from "../../contexts/interfaces";
import { checkDateStatus } from "../../scripts/checkDateStatus";
import MainResultsTableWaiting from "../../components/Main/MainResults/MainResultsTableWaiting/MainResultsTableWaiting";
import { getEmployee } from "../../scripts/dataHandlers";
import { useUserContext } from "../../contexts/UserContext";
import ReactLoading from "react-loading";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useErrorContext } from "../../contexts/ErrorContext";

interface Props {
  selectedUnit: Unit | undefined;
  setSelectedUnit: (val: Unit | undefined) => void;
}

const MainPage = ({ selectedUnit, setSelectedUnit }: Props) => {
  //const { setChosenEmployee } = useChosenEmployeeContext();
  const { user } = useUserContext();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedFilter, setSelectedFilter] = useState<string>("");
  const [unitData, setUnitData] = useState<Unit[] | undefined>([]);
  const [employeesInUnitState, setEmployeesInUnitState] = useState<Employee[]>(
    []
  );

  const [activePermits, setActivePermits] = useState<Employee[]>([]);
  const [expiringPermits, setExpiringPermits] = useState<Employee[]>([]);
  const [expiredPermits, setExpiredPermits] = useState<Employee[]>([]);
  const [unfinishedForms, setUnfinishedForms] = useState<Employee[]>([]);
  const [waitingForms, setWaitingForms] = useState<Employee[]>([]);

  const [searchEmployeeId, setSearchEmployeeId] = useState<
    number | undefined
  >();

  const { handleError } = useErrorContext();

  const { t } = useTranslation();

  // This useEffect reacts to user changes, setting unit data as options for select
  // and first one from array as default selected unit
  useEffect(() => {
    if (user) {
      // Set unit data in stateholder to setup default options for unit selection
      setUnitData([...user.units]);
      // Set selected unit, default is first one in units array
      !selectedUnit && setSelectedUnit({ ...user.units[0] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Set userData as default setup
  useEffect(() => {
    setIsLoading(true);

    // Clear search id if this component is reloaded (unitData changes)
    setSearchEmployeeId(undefined);

    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();

    const getEmployeesByUnitId = async () => {
      // Call getEmployee with only unitId, fetching all employees in selected unit
      const response =
        user &&
        selectedUnit &&
        (await getEmployee(user.id, selectedUnit.id, undefined, null, source));

      // Set response.data in employeesInUnit, if data is undefined, set empty array
      const employeesInUnit: Employee[] = response?.data?.result || [];

      if (response?.status === 200 && employeesInUnit) {
        // *** Successful response

        // Set employees that have active permits
        setActivePermits([
          ...employeesInUnit.filter(
            (employee) =>
              checkDateStatus(employee.permitsValidUntil) === "active" ||
              checkDateStatus(employee.permitsValidUntil) === "expiring"
          ),
        ]);
        // Set employees that have expiring permits
        setExpiringPermits([
          ...employeesInUnit.filter(
            (employee) =>
              checkDateStatus(employee.permitsValidUntil) === "expiring"
          ),
        ]);
        // Set employees that have expired permits
        setExpiredPermits([
          ...employeesInUnit.filter(
            (employee) =>
              checkDateStatus(employee.permitsValidUntil) === "expired"
          ),
        ]);
        // Set forms that are in unfinishedForms status
        setUnfinishedForms([
          ...employeesInUnit.filter(
            (employee) =>
              employee.formStatus === "education" ||
              employee.formStatus === "permits" ||
              employee.formStatus === "approval"
          ),
        ]);
        // Set forms that are in waitingForms status
        setWaitingForms([
          ...employeesInUnit.filter(
            (employee) => employee.formStatus === "waiting"
          ),
        ]);

        // Set in state that is used for json -> xslx export and for search
        setEmployeesInUnitState([...employeesInUnit]);

        // Default filter is activePermits
        setSelectedFilter("activePermits");

        setIsLoading(false);
      } else {
        handleError(response, "GetEmployeeFailed");
      }
    };

    // If user and selectedUnit is set, get data
    user && selectedUnit && getEmployeesByUnitId();

    return () => source.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnit, user]);

  if (!isLoading && selectedUnit && unitData) {
    return (
      <div className="mainContainer">
        <div className={classes.container}>
          <MainTitle
            setSearchEmployeeId={setSearchEmployeeId}
            employeesInUnit={employeesInUnitState}
          />
          <MainTop
            selectedUnit={selectedUnit}
            setSelectedUnit={setSelectedUnit}
            unitData={unitData}
            employeesInUnit={employeesInUnitState}
          />
          <MainStatusFilters
            activePermits={activePermits}
            expiringPermits={expiringPermits}
            expiredPermits={expiredPermits}
            unfinishedForms={unfinishedForms}
            waitingForms={waitingForms}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            searchEmployeeId={searchEmployeeId}
          />
          {/* If chosen filter tab is waitingForms or unfinishedForms, show only list of 
                    waiting or unfinished form items instead of results table */}
          {selectedFilter === "waitingForms" ||
          selectedFilter === "unfinishedForms" ? (
            <MainResultsTableWaiting
              forms={
                selectedFilter === "waitingForms"
                  ? waitingForms
                  : unfinishedForms
              }
              selectedFilter={selectedFilter}
              selectedUnitId={selectedUnit.id}
              searchEmployeeId={searchEmployeeId}
            />
          ) : (
            <MainResultsTable
              activePermits={activePermits}
              expiringPermits={expiringPermits}
              expiredPermits={expiredPermits}
              unfinishedForms={unfinishedForms}
              waitingForms={waitingForms}
              selectedFilter={selectedFilter}
              searchEmployeeId={searchEmployeeId}
              selectedUnitId={selectedUnit.id}
            />
          )}
        </div>
      </div>
    );
  } else {
    return (
      <div className={classes.loadingContainer}>
        <ReactLoading
          type={"spokes"}
          color={"#ED1B24"}
          height={"50px"}
          width={"50px"}
        />

        <div className={classes.loadingText}>{t("mainPage.loading")}</div>
      </div>
    );
  }
};

export default MainPage;
